import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';
import { ebooks } from '@/mocks/ebooks';

type Segment = {
  title: string;
  description: string;
  icon: string;
  path: string;
  tag?: string;
  tagColor?: string;
  ebookSlug?: string;
};

type Category = {
  id: string;
  label: string;
  icon: string;
  segments: Segment[];
};

const categories: Category[] = [
  {
    id: 'profissional',
    label: 'Serviços Profissionais',
    icon: 'ri-briefcase-4-line',
    segments: [
      {
        title: 'Advogados',
        description: 'Escritórios de advocacia e bancas jurídicas. Mesas de diretoria, salas de reunião e recepções que transmitem autoridade.',
        icon: 'ri-scales-3-line',
        path: '/advogados',
        tag: 'Popular',
        tagColor: 'bg-[#E8610A]',
        ebookSlug: 'escritorio-advocacia-que-fecha-contratos',
      },
      {
        title: 'Contabilidade',
        description: 'Escritórios contábeis e assessorias fiscais. Recepção, estações de trabalho ergonômicas e arquivo organizado.',
        icon: 'ri-bar-chart-box-line',
        path: '/contabilidade',
        ebookSlug: 'montando-escritorio-do-zero',
      },
      {
        title: 'Imobiliárias',
        description: 'Corretoras e incorporadoras. Ambiente que vende o sonho antes de mostrar o primeiro imóvel.',
        icon: 'ri-building-line',
        path: '/imobiliarias',
        ebookSlug: 'imobiliaria-escritorio-que-fecha',
      },
      {
        title: 'Coworking',
        description: 'Espaços colaborativos e hubs. Estações de trabalho que retêm membros e salas de reunião que geram receita.',
        icon: 'ri-community-line',
        path: '/coworking',
        ebookSlug: 'montando-escritorio-do-zero',
      },
    ],
  },
  {
    id: 'saude',
    label: 'Saúde e Bem-estar',
    icon: 'ri-heart-pulse-line',
    segments: [
      {
        title: 'Clínicas Médicas',
        description: 'Clínicas, laboratórios e consultórios. Materiais higienizáveis conformes com a ANVISA.',
        icon: 'ri-hospital-line',
        path: '/clinicas',
        tag: 'Popular',
        tagColor: 'bg-[#E8610A]',
        ebookSlug: 'clinicas-ambiente-que-cura',
      },
      {
        title: 'Autismo / TEA',
        description: 'Centros de atendimento e clínicas especializadas em TEA. Ambientes seguros e funcionais.',
        icon: 'ri-mental-health-line',
        path: '/autismo',
        ebookSlug: 'clinicas-ambiente-que-cura',
      },
      {
        title: 'Academias',
        description: 'Academias e centros fitness. Recepção que converte, vestiários organizados e sala de avaliação profissional.',
        icon: 'ri-heart-pulse-line',
        path: '/academia',
        ebookSlug: 'academia-ambiente-que-converte',
      },
      {
        title: 'Beleza e Estética',
        description: 'Salões, barbearias e clínicas estéticas. Bancadas resistentes a produtos químicos e recepção elegante.',
        icon: 'ri-scissors-line',
        path: '/beleza-estetica',
        ebookSlug: 'recepcao-que-vende',
      },
    ],
  },
  {
    id: 'comercio',
    label: 'Comércio e Varejo',
    icon: 'ri-store-2-line',
    segments: [
      {
        title: 'Farmácias',
        description: 'Farmácias e drogarias. Gôndolas que aumentam o ticket médio e balcões conformes com a ANVISA.',
        icon: 'ri-medicine-bottle-line',
        path: '/farmacias',
        ebookSlug: 'farmacia-layout-que-vende',
      },
      {
        title: 'Pet Shop',
        description: 'Pet shops e clínicas veterinárias. Expositores que vendem mais e materiais resistentes à umidade.',
        icon: 'ri-heart-line',
        path: '/pet-shop',
        ebookSlug: 'recepcao-que-vende',
      },
      {
        title: 'Gastronomia',
        description: 'Restaurantes, lanchonetes e food service. Mobiliário resistente ao uso intenso da gastronomia.',
        icon: 'ri-restaurant-line',
        path: '/gastronomia',
        ebookSlug: 'recepcao-que-vende',
      },
    ],
  },
  {
    id: 'institucional',
    label: 'Institucional e Educação',
    icon: 'ri-government-line',
    segments: [
      {
        title: 'Igrejas',
        description: 'Igrejas e templos religiosos. Longarinas estofadas confortáveis para cultos de 3 horas.',
        icon: 'ri-building-4-line',
        path: '/igrejas',
        tag: 'Popular',
        tagColor: 'bg-[#E8610A]',
      },
      {
        title: 'Escolar',
        description: 'Escolas, creches e universidades. Mobiliário pedagógico resistente ao uso intenso.',
        icon: 'ri-school-line',
        path: '/escolar',
        ebookSlug: 'escola-do-futuro-moveis-escolares-2026',
      },
      {
        title: 'Agronegócio',
        description: 'Cooperativas, fazendas e empresas rurais. Mobiliário robusto para o ambiente do agro.',
        icon: 'ri-plant-line',
        path: '/agronegocio',
        ebookSlug: 'montando-escritorio-do-zero',
      },
    ],
  },
  {
    id: 'corporativo',
    label: 'Corporativo e Indústria',
    icon: 'ri-building-2-line',
    segments: [
      {
        title: 'Tecnologia',
        description: 'Empresas de TI, startups e coworkings tech. Ambientes modernos que atraem e retêm talentos.',
        icon: 'ri-computer-line',
        path: '/tecnologia',
        ebookSlug: 'guia-nr17-ergonomia-escritorio',
      },
      {
        title: 'Costura / Confecção',
        description: 'Confecções, ateliês e indústria têxtil. Mobiliário funcional para o chão de fábrica e escritório.',
        icon: 'ri-scissors-cut-line',
        path: '/costura',
        ebookSlug: 'montando-escritorio-do-zero',
      },
      {
        title: 'Marcenaria Corporativa',
        description: 'Projetos de marcenaria sob medida para empresas. Do projeto 3D à instalação.',
        icon: 'ri-hammer-line',
        path: '/marcenaria-corporativa',
      },
      {
        title: 'Turnkey',
        description: 'Projeto completo de escritório: do planejamento à entrega das chaves. Tudo incluso.',
        icon: 'ri-key-line',
        path: '/turnkey',
        tag: 'Completo',
        tagColor: 'bg-emerald-600',
      },
    ],
  },
  {
    id: 'hospitalidade',
    label: 'Hospitalidade',
    icon: 'ri-hotel-bed-line',
    segments: [
      {
        title: 'Hotelaria',
        description: 'Hotéis, pousadas e resorts. Mobiliário que transforma hóspedes em avaliações 5 estrelas.',
        icon: 'ri-hotel-bed-line',
        path: '/hotelaria',
        ebookSlug: 'hotelaria-5-estrelas',
      },
      {
        title: 'Box Santa Cruz',
        description: 'Solução especial para Santa Cruz do Capibaribe. Mobiliário para o polo têxtil.',
        icon: 'ri-store-line',
        path: '/box-santa-cruz',
      },
    ],
  },
];

const allSegments = categories.flatMap(c => c.segments);

export default function SegmentosPage() {
  const [activeCategory, setActiveCategory] = useState<string>('todos');
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [ebookCopied, setEbookCopied] = useState<string | null>(null);

  useSEO({
    title: 'Segmentos Atendidos | Home Office Premium – Mobiliário Corporativo Caruaru PE',
    description: 'Conheça todos os segmentos atendidos pela Home Office Premium: advogados, clínicas, igrejas, academias, farmácias, pet shops, hotelaria e muito mais. Projeto 3D gratuito para cada segmento.',
    keywords: 'segmentos mobiliário corporativo Caruaru, nichos mobiliário Pernambuco, mobiliário para empresas Agreste PE',
    path: '/segmentos',
  });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    const schemaId = 'schema-segmentos';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Segmentos Atendidos – Home Office Premium',
      description: 'Todos os segmentos atendidos pela Home Office Premium: advogados, clínicas, igrejas, academias, farmácias, pet shops, hotelaria e muito mais.',
      url: `${siteUrl}/segmentos`,
      publisher: { '@type': 'Organization', name: 'Home Office Premium', url: siteUrl },
    });

    const breadcrumbId = 'schema-segmentos-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Segmentos Atendidos', item: `${siteUrl}/segmentos` },
      ],
    });

    const faqId = 'schema-segmentos-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Quais segmentos a Home Office Premium atende em Caruaru PE?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Home Office Premium atende escritórios de advocacia, clínicas médicas, igrejas, academias, farmácias, pet shops, hotéis, pousadas, escolas, coworkings, empresas de tecnologia, contabilidade, imobiliárias, restaurantes e muito mais. Cada segmento tem uma página específica com argumentos e soluções personalizadas.' },
        },
        {
          '@type': 'Question',
          name: 'A Home Office Premium faz projeto 3D gratuito para todos os segmentos?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Oferecemos projeto 3D gratuito para todos os segmentos atendidos. Nossa equipe visita o espaço, entende as necessidades específicas do negócio e apresenta um projeto completo antes de qualquer investimento.' },
        },
        {
          '@type': 'Question',
          name: 'Como solicitar mobiliário para um segmento específico em Caruaru?',
          acceptedAnswer: { '@type': 'Answer', text: 'Acesse a página do seu segmento no site da Home Office Premium, veja as soluções específicas e entre em contato pelo WhatsApp (81) 98203-0285 ou pelo formulário de contato. Respondemos em até 2 horas.' },
        },
      ],
    });

    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  const baseUrl = 'https://homeofficepremium.com';

  const handleCopy = (path: string) => {
    const url = `${baseUrl}${path}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(path);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleCopyEbook = (slug: string) => {
    const url = `${baseUrl}/ebooks/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setEbookCopied(slug);
      setTimeout(() => setEbookCopied(null), 2500);
    });
  };

  const filteredCategories = categories
    .map(cat => ({
      ...cat,
      segments: cat.segments.filter(s =>
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        s.description.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(cat =>
      (activeCategory === 'todos' || cat.id === activeCategory) &&
      cat.segments.length > 0
    );

  const displayedSegments = search
    ? allSegments.filter(s =>
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        s.description.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 bg-[#fafafa] border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#E8610A]/10 text-[#E8610A] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Segmentos Atendidos
            </span>
            <h1 className="text-gray-900 text-4xl md:text-5xl font-bold leading-tight mb-4">
              Encontre a página certa para cada cliente.
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed mb-8">
              Selecione o segmento do seu cliente, copie o link e envie direto no WhatsApp. Cada página foi criada com argumentos específicos para aquele perfil de negócio.
            </p>

            {/* Search */}
            <div className="relative max-w-lg">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-base"></i>
              </div>
              <input
                type="text"
                placeholder="Buscar segmento... (ex: advogado, clínica, academia)"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#E8610A] focus:ring-1 focus:ring-[#E8610A]/20 transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center gap-6 md:gap-10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                <i className="ri-layout-grid-line text-[#E8610A] text-sm"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">{allSegments.length} segmentos</p>
                <p className="text-gray-400 text-xs">páginas disponíveis</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                <i className="ri-links-line text-[#E8610A] text-sm"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Link direto</p>
                <p className="text-gray-400 text-xs">copie e envie no WhatsApp</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                <i className="ri-whatsapp-line text-[#E8610A] text-sm"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Copy personalizado</p>
                <p className="text-gray-400 text-xs">argumentos por segmento</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category filter */}
      {!search && (
        <section className="bg-white sticky top-[56px] z-30 border-b border-gray-100 py-3">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setActiveCategory('todos')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === 'todos'
                    ? 'bg-[#E8610A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className="ri-apps-line text-xs"></i>
                Todos
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-[#E8610A] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Segments grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">

          {/* Search results */}
          {search && displayedSegments && (
            <div>
              <p className="text-gray-500 text-sm mb-6">
                <span className="font-semibold text-gray-900">{displayedSegments.length}</span> resultado{displayedSegments.length !== 1 ? 's' : ''} para &quot;{search}&quot;
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {displayedSegments.map(seg => (
                  <SegmentCard key={seg.path} seg={seg} baseUrl={baseUrl} copied={copied} ebookCopied={ebookCopied} onCopy={handleCopy} onCopyEbook={handleCopyEbook} />
                ))}
              </div>
            </div>
          )}

          {/* Categorized results */}
          {!search && (
            <div className="flex flex-col gap-14">
              {filteredCategories.map(cat => (
                <div key={cat.id}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-xl">
                      <i className={`${cat.icon} text-[#E8610A] text-lg`}></i>
                    </div>
                    <div>
                      <h2 className="text-gray-900 font-bold text-lg">{cat.label}</h2>
                      <p className="text-gray-400 text-xs">{cat.segments.length} segmento{cat.segments.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {cat.segments.map(seg => (
                      <SegmentCard key={seg.path} seg={seg} baseUrl={baseUrl} copied={copied} ebookCopied={ebookCopied} onCopy={handleCopy} onCopyEbook={handleCopyEbook} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA bottom */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-xs font-bold uppercase tracking-widest mb-3">Não encontrou o segmento?</p>
          <h2 className="text-white text-2xl md:text-3xl font-bold mb-4">
            Atendemos qualquer tipo de empresa.
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            Se o segmento do seu cliente não está listado aqui, fale com a gente. Temos soluções para qualquer tipo de negócio — e podemos criar uma página personalizada para o segmento que você precisa.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20preciso%20de%20uma%20p%C3%A1gina%20para%20um%20segmento%20espec%C3%ADfico!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-8 py-3.5 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-whatsapp-line text-base"></i>
              Solicitar novo segmento
            </a>
            <Link
              to="/contato"
              className="inline-flex items-center gap-2 justify-center bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-3.5 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-mail-line text-base"></i>
              Falar com a equipe
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

type SegmentCardProps = {
  seg: Segment;
  baseUrl: string;
  copied: string | null;
  ebookCopied: string | null;
  onCopy: (path: string) => void;
  onCopyEbook: (slug: string) => void;
};

function SegmentCard({ seg, baseUrl, copied, ebookCopied, onCopy, onCopyEbook }: SegmentCardProps) {
  const isCopied = copied === seg.path;
  const isEbookCopied = ebookCopied === seg.ebookSlug;
  const fullUrl = `${baseUrl}${seg.path}`;
  const ebookData = seg.ebookSlug ? ebooks.find(e => e.slug === seg.ebookSlug) : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden hover:border-[#E8610A]/40 transition-all duration-200 group">
      {/* Card header */}
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="w-11 h-11 flex items-center justify-center bg-[#E8610A]/10 rounded-xl flex-shrink-0">
            <i className={`${seg.icon} text-[#E8610A] text-xl`}></i>
          </div>
          {seg.tag && (
            <span className={`${seg.tagColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap`}>
              {seg.tag}
            </span>
          )}
        </div>
        <div>
          <h3 className="text-gray-900 font-bold text-base leading-snug mb-1">{seg.title}</h3>
          <p className="text-gray-500 text-xs leading-relaxed">{seg.description}</p>
        </div>
      </div>

      {/* Ebook badge — se tiver ebook vinculado */}
      {ebookData && (
        <div className="px-5 pb-3">
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-center gap-2">
            <i className="ri-book-open-line text-amber-500 text-xs flex-shrink-0"></i>
            <span className="text-amber-700 text-xs font-medium truncate flex-1">{ebookData.title}</span>
          </div>
        </div>
      )}

      {/* URL preview */}
      <div className="px-5 pb-3">
        <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2">
          <i className="ri-link text-gray-400 text-xs flex-shrink-0"></i>
          <span className="text-gray-400 text-xs truncate font-mono">{fullUrl}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 pb-5 flex flex-col gap-2">
        {/* Linha 1: copiar link da página + abrir */}
        <div className="flex gap-2">
          <button
            onClick={() => onCopy(seg.path)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
              isCopied
                ? 'bg-emerald-500 text-white'
                : 'bg-[#E8610A] hover:bg-[#C4500A] text-white'
            }`}
          >
            <i className={`${isCopied ? 'ri-check-line' : 'ri-clipboard-line'} text-sm`}></i>
            {isCopied ? 'Copiado!' : 'Copiar link'}
          </button>
          <Link
            to={seg.path}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer flex-shrink-0"
            title="Abrir página"
          >
            <i className="ri-external-link-line text-gray-500 text-sm"></i>
          </Link>
        </div>

        {/* Linha 2: enviar ebook (só se tiver ebook) */}
        {ebookData && (
          <div className="flex gap-2">
            <button
              onClick={() => onCopyEbook(ebookData.slug)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                isEbookCopied
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50'
              }`}
            >
              <i className={`${isEbookCopied ? 'ri-check-line' : 'ri-book-open-line'} text-sm`}></i>
              {isEbookCopied ? 'Link copiado!' : 'Copiar link do ebook'}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Olá! Preparei este material gratuito especialmente para você: ${ebookData.title}. Acesse e baixe gratuitamente: https://homeofficepremium.com/ebooks/${ebookData.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center bg-[#25D366] hover:bg-[#20B858] rounded-lg transition-colors cursor-pointer flex-shrink-0"
              title="Enviar ebook pelo WhatsApp"
            >
              <i className="ri-whatsapp-line text-white text-sm"></i>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
